import { useRef } from 'react';
import { useStore } from '../../../../hooks';

/**
 * Custom hook to handle file uploads for Knowledge module
 * This version works without requiring DrivesProvider
 * @param {Object} options - Configuration options
 * @param {Function} options.onUploadComplete - Callback when upload is complete
 * @param {Function} options.onUploadStart - Callback when upload starts
 * @param {string} options.folderId - Optional folder ID to upload files to
 * @param {string} options.vaultUuid - Optional vault UUID for upload (required if not using DrivesProvider)
 * @returns {Object} - Utilities to handle file uploads
 */
export const useFileUpload = ({ onUploadComplete, onUploadStart, folderId, vaultUuid } = {}) => {
  const { documentsStore, authStore, drivesStore } = useStore();
  const fileInputRef = useRef(null);

  const processFiles = async (files, overrideOptions = {}) => {
    if (!files || files.length === 0) return;

    onUploadStart?.();

    try {
      // Try to get vaultUuid from multiple sources
      let targetVaultUuid = 
        vaultUuid || 
        overrideOptions?.vaultUuid;

      // If not provided, try to get from drivesStore
      if (!targetVaultUuid) {
        // If drives are loading, wait for them to finish
        if (drivesStore?.isLoading) {
          // Wait up to 5 seconds for drives to load
          let attempts = 0;
          const maxAttempts = 50; // 5 seconds with 100ms intervals
          while (drivesStore.isLoading && attempts < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, 100));
            attempts++;
          }
        }

        // Try to fetch drives if not loaded
        if (drivesStore && (!drivesStore.drives || drivesStore.drives.length === 0) && !drivesStore.isLoading) {
          await drivesStore.fetchDrives();
        }

        // Get vaultUuid from first drive
        if (drivesStore?.drives?.length > 0) {
          targetVaultUuid = drivesStore.drives[0]?.id;
        }
      }

      if (!targetVaultUuid) {
        throw new Error('vaultUuid is required for file upload. Unable to load drives. Please try again or provide vaultUuid parameter.');
      }

      const uploadOptions = {
        vaultUuid: targetVaultUuid,
        userId: authStore.userId,
        tenantId: '58145bef-4f71-468a-bded-1823d2a08f58',
        ...overrideOptions
      };

      const targetFolderId =
        overrideOptions?.folderId !== undefined
          ? overrideOptions.folderId
          : folderId;

      if (targetFolderId) {
        uploadOptions.folderId = targetFolderId;
      }

      const result = await documentsStore.uploadMultipleFiles(files, uploadOptions);

      // Note: We don't reload Drives data here since this is for Knowledge module
      // The parent component should handle refreshing its own data if needed

      onUploadComplete?.();
      
      // Return the upload result for the caller to use
      return result;
    } catch (error) {
      console.error('Error uploading files:', error);
      throw error; // Re-throw so the component can handle it
    }
  };

  const handleFileInputChange = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    await processFiles(files);
    
    // Clear input to allow uploading the same file again
    event.target.value = '';
  };

  const handleDrop = async (event) => {
    const files = Array.from(event.dataTransfer?.files || []);
    if (files.length === 0) return;

    await processFiles(files);
  };

  const openFileSelector = () => {
    if (fileInputRef.current && !fileInputRef.current.dataset.opening) {
      fileInputRef.current.dataset.opening = 'true';
      fileInputRef.current.click();
      setTimeout(() => {
        if (fileInputRef.current) {
          delete fileInputRef.current.dataset.opening;
        }
      }, 100);
    }
  };

  const retryUpload = async (temporaryDocument) => {
    if (!temporaryDocument) return;

    const fileToRetry = temporaryDocument.file || temporaryDocument.originalFile;
    if (!fileToRetry) {
      console.warn('No original file available for retry:', temporaryDocument.name);
      return;
    }

    const identifier = temporaryDocument.uploadId || temporaryDocument.name;
    // Remove previous temporary entry before retrying
    documentsStore.removeTemporaryDocument(identifier);

    await processFiles([fileToRetry], temporaryDocument.retryOptions || {});
  };

  // Props for the file input
  const fileInputProps = {
    ref: fileInputRef,
    type: 'file',
    multiple: true,
    accept: '.pdf,.doc,.docx,.jpg,.png,.xlsx',
    onChange: handleFileInputChange,
    style: { display: 'none' }
  };

  return {
    fileInputRef,
    fileInputProps,
    handleFileInputChange,
    handleDrop,
    openFileSelector,
    processFiles,
    retryUpload
  };
};
